import { useRef } from "react";
import "./portfolio.scss";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { useMediaQuery } from "react-responsive";

const items = [
  {
    id: 1,
    title: "Sapient Imobiliare",
    img: "https://spectrum-studios.b-cdn.net/sapient.webp",
    desktopDesc:
        "We partnered with Sapient Imobiliare to showcase their premium real estate portfolio through high-quality videography and drone footage. Our team created cinematic property tours that highlight the unique features of each property, engaging potential buyers and boosting Sapient's online presence. This collaboration significantly enhanced their marketing strategy, emphasizing professionalism and luxury in every frame.",
    mobileDesc:
        "Showcased premium real estate with cinematic property tours, boosting Sapient's online presence and emphasizing luxury.",
  },
  {
    id: 2,
    title: "Hilton Oradea",
    img: "https://spectrum-studios.b-cdn.net/hilton.webp",
    desktopDesc:
        "For Hilton Oradea, we provided top-notch content creation services to promote their luxury hospitality offerings. Our team developed a series of visually stunning promotional videos and lifestyle imagery that captured the elegance and sophistication of the Hilton brand. The content was tailored for social media campaigns, helping Hilton Oradea attract a wider audience and establish a strong digital presence.",
    mobileDesc:
        "Created stunning promotional videos and imagery, enhancing Hilton's digital presence and attracting a wider audience.",
  },
  {
    id: 3,
    title: "Huta Slavia",
    img: "https://spectrum-studios.b-cdn.net/huta.webp",
    desktopDesc:
        "Our collaboration with Huta Slavia focused on creating captivating content for their eco-tourism initiatives. We produced immersive videos featuring the natural beauty of the location, drone shots of the breathtaking landscapes, and engaging stories about their sustainable practices. The result was a powerful visual narrative that resonated with environmentally conscious travelers, helping Huta Slavia increase bookings and brand loyalty.",
    mobileDesc:
        "Produced immersive eco-tourism content, highlighting natural beauty and sustainability to engage conscious travelers.",
  },
  {
    id: 4,
    title: "Giorgio Mare",
    img: "https://spectrum-studios.b-cdn.net/giorgio.webp",
    desktopDesc:
        "Giorgio Mare enlisted our services to promote their premium store specializing in authentic Italian seafood and culinary delicacies. We crafted a series of captivating videos highlighting their high-quality products, artisanal offerings, and the rich traditions behind their flavors. Through a blend of elegant close-ups of their finest specialties and inviting visuals of the store's ambiance, we helped Giorgio Mare connect with their audience, enhancing both in-store visits and online engagement.",
    mobileDesc:
        "Showcased the store's premium Italian seafood and specialties through captivating videos.",
  },
  {
    id: 5,
    title: "QubMenu",
    img: "https://spectrum-studios.b-cdn.net/qubmenu.webp",
    desktopDesc:
        "We collaborated with QubMenu, a pioneer in digital menus for restaurants, to create engaging video and photography content. Our team captured the innovative design and seamless functionality of their products, showcasing how QubMenu enhances the dining experience. The final visuals effectively highlight their technology, supporting QubMenu's marketing and brand promotion efforts.",
    mobileDesc:
        "Highlighted QubMenu's innovative digital menus through engaging visuals showcasing their seamless functionality.",
  },
  {
    id: 6,
    title: "AutoGrill E60",
    img: "https://spectrum-studios.b-cdn.net/autogrill.webp",
    desktopDesc:
        "Located in the peaceful village of Bucea, our restaurant serves authentic Romanian cuisine in a traditional setting. We offer hearty, homemade dishes prepared with locally sourced ingredients, following time-honored recipes. The rustic interior with wooden furnishings and folk decorations creates a warm atmosphere where guests can experience genuine Romanian hospitality while enjoying the natural beauty of our village surroundings.",
    mobileDesc:
        "Traditional Romanian restaurant in Bucea village offering authentic cuisine and warm countryside hospitality.",
  },
  {
    id: 7,
    title: "Drink Shop & The Store",
    img: ["https://spectrum-studios.b-cdn.net/Clients/Drink%20Shop.png", "https://spectrum-studios.b-cdn.net/Clients/The%20Store.png"],
    mobileImageScale: true,
    desktopDesc:
        "We collaborated with Drink Shop & The Store to create compelling visual content that showcases their unique retail experience. Our team produced high-quality videos and photography highlighting their product offerings, store ambiance, and brand identity. The resulting content effectively communicates their value proposition across social media platforms and marketing campaigns, helping increase customer engagement and store traffic.",
    mobileDesc:
        "Created compelling visual content for Drink Shop & The Store, showcasing their products and brand identity to boost engagement.",
  },
  {
    id: 8,
    title: "Bricks",
    img: "https://spectrum-studios.b-cdn.net/Clients/LOGO%20Bricks.png",
    logoClass: "whiteBg",
    desktopDesc:
        "For Bricks brunch restaurant, we developed a mouth-watering content strategy that captures the essence of their culinary creations. Our team produced vibrant food photography and atmospheric videos that highlight the restaurant's unique ambiance and delicious offerings. The visual storytelling emphasizes the quality ingredients, creative presentations, and inviting atmosphere that make Bricks a standout brunch destination.",
    mobileDesc:
        "Developed appetizing visuals for Bricks brunch restaurant, showcasing their culinary creations and inviting atmosphere.",
  },
  {
    id: 9,
    title: "Vest Invest",
    img: "https://spectrum-studios.b-cdn.net/Clients/Sigla-Vestinvest.jpg",
    logoClass: "vestinvestLogo",
    desktopDesc:
        "Our partnership with Vest Invest real estate focused on elevating their property marketing through professional videography and photography. We created immersive property tours, aerial drone footage, and compelling visual narratives that showcase their premium real estate portfolio. The resulting content helps potential buyers envision themselves in these spaces, strengthening Vest Invest's market position and accelerating their sales process.",
    mobileDesc:
        "Elevated Vest Invest's real estate marketing with immersive property tours and aerial footage to accelerate their sales process.",
  },
  {
    id: 10,
    title: "Nectora",
    img: "https://spectrum-studios.b-cdn.net/Clients/NECTORA%20PNG.png",
    logoClass: "whiteBg nectoraLogo",
    desktopDesc:
        "Working with Nectora real estate, we delivered sophisticated visual content that reflects the quality of their property developments. Our team produced cinematic videos and architectural photography that highlight the unique features and design elements of their properties. This tailored content strategy has enhanced Nectora's brand perception, helping them attract discerning clients and distinguish themselves in the competitive real estate market.",
    mobileDesc:
        "Created sophisticated visual content for Nectora real estate, highlighting their unique property developments to attract discerning clients.",
  },
  {
    id: 11,
    title: "NextGen",
    img: "https://spectrum-studios.b-cdn.net/Clients/Logo%20NextGen.png",
    desktopDesc:
        "For the NextGen conference, we provided comprehensive event coverage through dynamic videography and photography. Our team captured keynote presentations, panel discussions, networking moments, and the overall energy of this forward-thinking event. The resulting content serves both as a valuable record of the conference and as powerful promotional material for future NextGen events, helping to expand their reach and audience engagement.",
    mobileDesc:
        "Provided dynamic event coverage for NextGen conference, capturing key moments to serve as promotional material for future events.",
  },
];

const Single = ({ item, isMobile }) => {
  const ref = useRef();
  const { scrollYProgress } = useScroll({
    target: ref,
  });
  const y = useTransform(scrollYProgress, [0, 1], [-300, 300]);

  return (
      <section>
        <div className="container">
          <div className="wrapper">
            <div className="imageContainer" ref={ref}>
              {Array.isArray(item.img) ? (
                  <div className="dualLogoContainer">
                    {item.img.map((imgSrc, index) => (
                        <img
                            key={index}
                            src={imgSrc}
                            alt={`${item.title} logo ${index + 1}`}
                            className={`dualLogo ${item.mobileImageScale && isMobile ? 'mobileScale' : ''}`}
                        />
                    ))}
                  </div>
              ) : (
                  <img
                      src={item.img}
                      alt={item.title}
                      className={item.logoClass || ""}
                  />
              )}
            </div>
            <motion.div className="textContainer" style={{ y }}>
              <h2>{item.title}</h2>
              <p>{isMobile ? item.mobileDesc : item.desktopDesc}</p>
            </motion.div>
          </div>
        </div>
      </section>
  );
};

const Portfolio = () => {
  const ref = useRef();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["end end", "start start"],
  });
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
  });

  // Detect screen size
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
      <div className="portfolio" ref={ref}>
        <div className="progress">
          <h1>
            Featured <span className="text-white">Works</span>
          </h1>
          <motion.div style={{ scaleX }} className="progressBar"></motion.div>
        </div>
        {items.map((item) => (
            <Single item={item} isMobile={isMobile} key={item.id} />
        ))}
      </div>
  );
};

export default Portfolio;
